import type { Site } from '@sarnia/shared';

export type AutosaveState = 'saving' | 'saved' | 'offline' | 'error';

export interface AutosaveCallbacks {
  save: (site: Site, version: number, siteId: string) => Promise<void>;
  onStateChange: (state: AutosaveState, details?: { version?: number; message?: string }) => void;
  debounceMs?: number;
}

export class AutosaveController {
  private latestSite: Site | null = null;
  private latestVersion = 0;
  private latestSiteId = '';
  private timer: ReturnType<typeof setTimeout> | null = null;
  private saveInFlight = false;
  private queueAfterSave = false;
  private readonly debounceMs: number;
  private readonly save: AutosaveCallbacks['save'];
  private readonly onStateChange: AutosaveCallbacks['onStateChange'];

  constructor(callbacks: AutosaveCallbacks) {
    this.save = callbacks.save;
    this.onStateChange = callbacks.onStateChange;
    this.debounceMs = callbacks.debounceMs ?? 1500;
  }

  schedule(site: Site, version: number, siteId: string): void {
    this.latestSite = site;
    this.latestVersion = version;
    this.latestSiteId = siteId;

    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, this.debounceMs);
  }

  cancel(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.latestSite = null;
    this.saveInFlight = false;
    this.queueAfterSave = false;
  }

  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (!this.latestSite) {
      return;
    }

    if (this.saveInFlight) {
      this.queueAfterSave = true;
      return;
    }

    this.saveInFlight = true;
    this.onStateChange('saving');

    const site = this.latestSite;
    const version = this.latestVersion;
    const siteId = this.latestSiteId;

    try {
      await this.save(site, version, siteId);
      this.saveInFlight = false;
      // Nothing changed while saving: forget it, so another flush (the Save button, version history) does not send the
      // same content again and bump the version for nothing.
      if (this.latestSite === site) {
        this.latestSite = null;
      }
      this.onStateChange('saved');

      if (this.queueAfterSave) {
        this.queueAfterSave = false;
        if (this.latestSite) {
          this.schedule(this.latestSite, this.latestVersion, this.latestSiteId);
        }
      }
    } catch (_error) {
      this.saveInFlight = false;
      this.queueAfterSave = false;
      // Do not rethrow: the store observes the save error via onStateChange.
    }
  }

  hasPendingSave(): boolean {
    return this.timer !== null || this.saveInFlight;
  }
}
